import { useState, useEffect } from 'react'
import { Routes, Route, useNavigate, useParams } from 'react-router-dom'
import {
  Camera, Video, Flower2, Mic, UtensilsCrossed, Shirt, Palette, Gem,
  Building2, Music2, Cake, ClipboardList, ChevronLeft,
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import FallingPetals from '../../components/FallingPetals'
import CupidIcon from '../../components/CupidIcon'
import VendorCard from '../../components/aisle/VendorCard'
import VendorProfilePage from './VendorProfilePage'
import CupidPage from './CupidPage'
import InquiryPage from './InquiryPage'

export const CATEGORIES = [
  { key:'photographer',   label:'Photographers',    Icon:Camera },
  { key:'videographer',   label:'Videographers',    Icon:Video },
  { key:'decorator',      label:'Decorators',       Icon:Flower2 },
  { key:'mc_dj',          label:'MCs & DJs',        Icon:Mic },
  { key:'caterer',        label:'Caterers',         Icon:UtensilsCrossed },
  { key:'aso_ebi',        label:'Aso Ebi & Fabric', Icon:Shirt },
  { key:'makeup',         label:'Makeup Artists',   Icon:Palette },
  { key:'bridal_fashion', label:'Bridal Fashion',   Icon:Gem },
  { key:'venue',          label:'Venues',           Icon:Building2 },
  { key:'live_band',      label:'Live Bands',       Icon:Music2 },
  { key:'cake',           label:'Cake Designers',   Icon:Cake },
  { key:'planner',        label:'Event Planners',   Icon:ClipboardList },
]
export const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map(c => [c.key, c.label]))

const GRID_CSS = `
  .aisle-arch-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:0.9rem; }
  .aisle-vendor-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:1rem; }
  @media (min-width:760px) {
    .aisle-arch-grid { grid-template-columns:repeat(4,1fr); gap:1.1rem; }
    .aisle-vendor-grid { grid-template-columns:repeat(3,1fr); }
  }
`

// ── Archway category card ──
function Archway({ cat, count, onClick }) {
  const [hover, setHover] = useState(false)
  const { Icon, label } = cat
  return (
    <button onClick={onClick}
      onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)}
      style={{
        display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-start',
        gap:'0.6rem', padding:'1.6rem 0.75rem 1.1rem', cursor:'pointer',
        background:'#FFFFFF', border:'1px solid #E5E0D8',
        borderRadius:'50% 50% 1.25rem 1.25rem',
        transform: hover ? 'translateY(-4px)' : 'none',
        boxShadow: hover ? '0 14px 30px rgba(196,130,106,0.18)' : '0 4px 14px rgba(0,0,0,0.05)',
        transition:'transform 0.18s ease, box-shadow 0.18s ease', minHeight:160,
      }}>
      <div style={{ width:54, height:54, borderRadius:'50%', background:'rgba(196,130,106,0.10)',
        display:'flex', alignItems:'center', justifyContent:'center', marginTop:'0.4rem' }}>
        <Icon size={32} color="#C4826A" />
      </div>
      <span style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:600, fontSize:'0.95rem',
        color:'#1A1A1A', textAlign:'center', lineHeight:1.2 }}>{label}</span>
      <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9BAF9B', fontWeight:700 }}>
        {count === undefined ? '…' : `${count} verified vendor${count===1?'':'s'}`}
      </span>
    </button>
  )
}

// ── The Aisle landing (hero + search + archways) ──
function AisleBrowse() {
  const navigate = useNavigate()
  const [counts, setCounts] = useState({})

  useEffect(() => {
    supabase.rpc('aisle_category_counts').then(({ data }) => {
      const m = {}; (data || []).forEach(r => { m[r.category] = Number(r.n) }); setCounts(m)
    }, () => {})
  }, [])

  return (
    <div>
      <style>{GRID_CSS}</style>

      {/* Hero */}
      <div style={{ position:'relative', overflow:'hidden', borderRadius:'1.5rem',
        background:'linear-gradient(135deg, #C4826A 0%, #D99F88 45%, #E9C3B4 100%)',
        padding:'3rem 1.5rem', textAlign:'center', marginBottom:'2rem' }}>
        <FallingPetals density={10} />
        <div style={{ position:'relative', zIndex:1 }}>
          <h1 style={{ fontFamily:'Gibs, serif', fontWeight:600, fontSize:'2.5rem',
            color:'#fff', margin:'0 0 0.5rem', textShadow:'0 2px 12px rgba(0,0,0,0.18)' }}>Walk the Aisle</h1>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'1rem', color:'rgba(255,255,255,0.95)',
            maxWidth:460, margin:'0 auto 1.5rem', lineHeight:1.6 }}>
            Find the people who will help make your day unforgettable.
          </p>
          <button onClick={() => navigate('/dashboard/aisle/cupid')}
            style={{ display:'inline-flex', alignItems:'center', gap:'0.5rem',
              background:'#fff', color:'#C4826A', border:'none', borderRadius:100,
              padding:'0.75rem 1.5rem', fontFamily:'Nunito Sans, sans-serif', fontWeight:700,
              fontSize:'0.9rem', cursor:'pointer', boxShadow:'0 6px 20px rgba(0,0,0,0.15)' }}>
            <CupidIcon size={20} variant="gradient" /> Let Cupid find your match
          </button>
        </div>
      </div>

      {/* Archways */}
      <div className="aisle-arch-grid">
        {CATEGORIES.map(cat => (
          <Archway key={cat.key} cat={cat} count={counts[cat.key] ?? 0}
            onClick={() => navigate(`/dashboard/aisle/c/${cat.key}`)} />
        ))}
      </div>
    </div>
  )
}

// ── Category browse (vendors in a category) ──
function AisleCategory() {
  const { category } = useParams()
  const navigate = useNavigate()
  const [vendors, setVendors] = useState(null)
  const [cityFilter, setCityFilter]     = useState('')
  const [budgetFilter, setBudgetFilter] = useState(0)

  useEffect(() => {
    let live = true
    setVendors(null); setCityFilter(''); setBudgetFilter(0)
    supabase.rpc('aisle_list_vendors', { p_category: category, p_city: null })
      .then(({ data }) => { if (live) setVendors(data || []) }, () => { if (live) setVendors([]) })
    return () => { live = false }
  }, [category])

  const cities = vendors ? [...new Set(vendors.map(v => v.city).filter(Boolean))].sort() : []
  const filtered = (vendors || []).filter(v => {
    if (cityFilter && v.city !== cityFilter) return false
    if (budgetFilter && v.starting_price && Number(v.starting_price) > budgetFilter) return false
    return true
  })
  const selectStyle = { fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', border:'1px solid #E5E0D8', borderRadius:100, padding:'0.5rem 0.9rem', outline:'none', background:'#fff', color:'#1A1A1A', cursor:'pointer' }

  return (
    <div>
      <style>{GRID_CSS}</style>
      <button onClick={() => navigate('/dashboard/aisle')}
        style={{ display:'flex', alignItems:'center', gap:'0.4rem', background:'none', border:'none',
          cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#6B6B6B',
          marginBottom:'1rem', padding:0 }}>
        <ChevronLeft size={16} /> The Aisle
      </button>
      <h1 style={{ fontFamily:'Gibs, serif', fontWeight:600, fontSize:'1.8rem',
        color:'#1A1A1A', margin:'0 0 1rem' }}>{CATEGORY_LABEL[category] || 'Vendors'}</h1>

      {/* Filters */}
      {vendors && vendors.length > 0 && (
        <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap', alignItems:'center', marginBottom:'1.25rem' }}>
          <select value={cityFilter} onChange={e=>setCityFilter(e.target.value)} style={selectStyle}>
            <option value="">All cities</option>
            {cities.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={budgetFilter} onChange={e=>setBudgetFilter(Number(e.target.value))} style={selectStyle}>
            <option value={0}>Any budget</option>
            <option value={250000}>Under ₦250,000</option>
            <option value={500000}>Under ₦500,000</option>
            <option value={1000000}>Under ₦1,000,000</option>
            <option value={2000000}>Under ₦2,000,000</option>
          </select>
          {(cityFilter || budgetFilter > 0) && (
            <button onClick={()=>{ setCityFilter(''); setBudgetFilter(0) }}
              style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:700, color:'#C4826A', background:'none', border:'none', cursor:'pointer' }}>
              Clear
            </button>
          )}
          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', marginLeft:'auto' }}>
            {filtered.length} of {vendors.length}
          </span>
        </div>
      )}

      {vendors === null ? (
        <p style={{ color:'#BCBCBC', textAlign:'center', padding:'3rem', fontFamily:'Nunito Sans, sans-serif' }}>Loading vendors…</p>
      ) : filtered.length === 0 ? (
        <p style={{ color:'#BCBCBC', textAlign:'center', padding:'3rem', fontFamily:'Nunito Sans, sans-serif' }}>
          {vendors.length === 0 ? 'No verified vendors in this category yet — check back soon.' : 'No vendors match these filters.'}
        </p>
      ) : (
        <div className="aisle-vendor-grid">
          {filtered.map(v => <VendorCard key={v.id} vendor={v} />)}
        </div>
      )}
    </div>
  )
}

export default function AislePage() {
  return (
    <Routes>
      <Route index element={<AisleBrowse />} />
      <Route path="c/:category" element={<AisleCategory />} />
      <Route path="vendor/:id" element={<VendorProfilePage />} />
      <Route path="inquiry/:id" element={<InquiryPage />} />
      <Route path="cupid" element={<CupidPage />} />
    </Routes>
  )
}
